import {useEffect, useState} from "react"
import {createUseStyles} from "react-jss"
import Button from "../components/Button"
import FullScreenLoadingIndicator from "../components/FullScreenLoadingIndicator"
import CreateOrUpdateInAppMessageModal from "../components/InAppMessage/CreateOrUpdateInAppMessageModal"
import InAppMessageList from "../components/InAppMessage/InAppMessageList"
import {titleMarginBottom} from "../constants/layout"
import useInAppMessageActions from "../hooks/useInAppMessageActions"
import {useAppSelector} from "../redux/hooks"
import {CreateOrUpdateInAppMessageModel, InAppMessageType} from "../redux/inAppMessageTypes"

const InAppMessages = () => {
  const styles = useStyles()

  const {getInAppMessages, createInAppMessage} = useInAppMessageActions()
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const loading = useAppSelector(state => state.inAppMessages.loading)
  const inAppMessagesOnPage = useAppSelector(state => state.inAppMessages.inAppMessagesOnPage)

  // Group messages by placement type
  const modalMessages = inAppMessagesOnPage.filter(msg => msg.type === InAppMessageType.Modal)
  const loginBannerMessages = inAppMessagesOnPage.filter(msg => msg.type === InAppMessageType.LoginScreenBanner)
  const payoutSystemStatusMessages = inAppMessagesOnPage.filter(msg => msg.type === InAppMessageType.PayoutSystemStatus)

  useEffect(() => {
    if (inAppMessagesOnPage.length === 0) {
      getInAppMessages()
    }
  }, [])

  const handleInAppMessageCreated = (model: CreateOrUpdateInAppMessageModel) => {
    createInAppMessage(model)
    setCreateModalVisible(false)
  }

  return (
    <div className={styles.container}>
      <h1>In-app messages</h1>
      <div className={styles.actionBar}>
        <Button title='Create new' onClick={() => setCreateModalVisible(true)}/>
      </div>

      <h2 className={styles.sectionHeader}>Modal Messages</h2>
      <InAppMessageList inAppMessages={modalMessages}/>

      <h2 className={styles.sectionHeader}>Login Screen Banner Messages</h2>
      <InAppMessageList inAppMessages={loginBannerMessages}/>

      <h2 className={styles.sectionHeader}>Payout System Status Messages</h2>
      <InAppMessageList inAppMessages={payoutSystemStatusMessages}/>

      <h2 className={styles.sectionHeader}>Info</h2>

      <h4>Modal messages</h4>
      <p>
        Modal messages are displayed in a modal dialog, presented right after the user has logged in.
        The modal messages do not take severity into account when displayed in the app.
      </p>

      <h4>Login screen banner messages</h4>
      <p>
        Login screen banner messages are displayed in the login screen.
        Based on severity, the message will be displayed in a different color (blue, yellow, light red, dark red).
        If a description and/or a link is provided, it will be displayed below the message, in an collapsible section,
        which will be expanded by default.
      </p>

      <p>
        Because no user is logged in at this time, a few restrictions apply:
      </p>

      <ul>
        <li>Messages can not be limited to a specific employer. Setting an employer will cause the message to be
          ignored.
        </li>
        <li>Messages can not be "read" by the user.</li>
        <li>They therefore ignore the "sticky" property (they will always be sticky).</li>
        <li>Only a single message can be displayed at a time, the highest priority message will be shown.</li>
        <li>It is not advisable to set a deep link url, as it would simply open the login page again.</li>
        <li>It is possible to set an external url to be opened when the message is clicked.</li>
      </ul>

      <h4>Payout System Status Messages</h4>
      <p>
        Payout system status messages are displayed as modal messages.
        These messages are created by the system, in order to inform users about disruptions in the Swish payout
        service. They are managed by the system, and will be created/published/unpublished automatically.
      </p>

      <CreateOrUpdateInAppMessageModal
        visible={createModalVisible}
        onAddClick={handleInAppMessageCreated}
        onCancelClick={() => setCreateModalVisible(false)}
      />
      <FullScreenLoadingIndicator visible={loading}/>
    </div>
  )
}

export default InAppMessages

const useStyles = createUseStyles({
  container: {
    '& h1': {
      marginBottom: titleMarginBottom
    }
  },
  sectionHeader: {
    marginTop: 30,
    marginBottom: 10
  },
  actionBar: {
    display: 'flex',
    marginTop: -30,
    flexDirection: 'row',
    columnGap: 10,
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
})
